import React, { type ReactElement } from 'react';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types.tsx';
import type { Project } from '@atlassian/jira-common-directory-v2/src/model/shareable-entities/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { Filter as FilterType } from '../../../model/index.tsx';
import messages from './messages.tsx';
import type { FilterSearchProps, ProjectSearchProps } from './types.tsx';

export type Props = {
	value: FilterType;
	renderFilterSearch: (props: FilterSearchProps) => ReactElement;
	renderProjectPicker: (props: ProjectSearchProps) => ReactElement;
	onChange: (arg1: FilterType, arg2: AnalyticsEvent) => void;
};

const Filter = (props: Props) => {
	const { formatMessage } = useIntl();
	const { renderFilterSearch, onChange, renderProjectPicker, value } = props;

	return fg('blu-3923_project_filter_in_boards_directory') ? (
		<Flex alignItems="end" gap="space.200">
			{renderFilterSearch({
				placeholder: formatMessage(messages.searchPlaceholder),
				value: '',
				hasAutoFocus: false,
				isCompact: true,
				increasedWidth: false,
				placeholderAlwaysVisible: true,
				isAlwaysExpanded: true,
				onChange: (text: string, analyticsEvent: AnalyticsEvent) => {
					onChange({ ...value, text }, analyticsEvent);
				},
			})}
			<Box xcss={filterLabelStyles}>
				{renderProjectPicker({
					onChange: (project: Project, analyticsEvent: AnalyticsEvent) => {
						onChange({ ...value, project }, analyticsEvent);
					},
					placeholder: formatMessage(messages.projectPlaceholder),
					label: formatMessage(messages.projectLabel),
					selectedProject: undefined,
					baseUrl: '',
					isCompact: true,
				})}
			</Box>
		</Flex>
	) : (
		<Flex alignItems="center" gap="space.200">
			{renderFilterSearch({
				onChange: (text: string, analyticsEvent: AnalyticsEvent) => {
					onChange({ ...value, text }, analyticsEvent);
				},
				placeholder: '',
				value: '',
				hasAutoFocus: false,
				isCompact: true,
				increasedWidth: false,
				placeholderAlwaysVisible: false,
				isAlwaysExpanded: true,
			})}
		</Flex>
	);
};

const filterLabelStyles = xcss({
	marginTop: 'space.negative.250',
});

export default Filter;
